import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { TrendingUp, TrendingDown, Visibility, Mouse, Download, Timeline as TimelineIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { metricsService, type Site, type TimeSeriesData } from '../services/metricsService';
import { useSiteStore } from '../store/site.store';
import MetricsChart from '../components/MetricsChart';
import DateRangePicker from '../components/DateRangePicker';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

interface TopMetric {
  url?: string;
  query?: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon, color }) => {
  const isPositive = change !== undefined && change >= 0;
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ mt: 1, mb: 1 }}>
              {value}
            </Typography>
            {change !== undefined && (
              <Box display="flex" alignItems="center" gap={0.5}>
                {isPositive ? (
                  <TrendingUp fontSize="small" color="success" />
                ) : (
                  <TrendingDown fontSize="small" color="error" />
                )}
                <Typography
                  variant="body2"
                  color={isPositive ? 'success.main' : 'error.main'}
                >
                  {isPositive ? '+' : ''}{change.toFixed(1)}%
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              borderRadius: 2,
              p: 1.5,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const selectedSite = useSiteStore(state => state.selectedSite);
  const selectedSiteId = selectedSite?.id; // Stable primitive value
  const [dateRange, setDateRange] = useState({
    startDate: '2026-01-01', // Sample data range
    endDate: '2026-01-05',   // Sample data range
  });

  // Use React Query for data fetching with proper caching and deduplication
  const queries = useQueries({
    queries: [
      {
        queryKey: ['metrics-timeseries', selectedSiteId, dateRange.startDate, dateRange.endDate],
        queryFn: () => metricsService.getTimeSeriesMetrics(
          selectedSiteId!,
          dateRange.startDate,
          dateRange.endDate
        ),
        enabled: !!selectedSiteId,
        staleTime: 5 * 60 * 1000, // 5 minutes - don't refetch if data is fresh
        gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
      },
      {
        queryKey: ['metrics-top-pages', selectedSiteId, dateRange.startDate, dateRange.endDate],
        queryFn: () => metricsService.getTopPages(
          selectedSiteId!,
          dateRange.startDate,
          dateRange.endDate,
          10
        ),
        enabled: !!selectedSiteId,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
      },
      {
        queryKey: ['metrics-top-queries', selectedSiteId, dateRange.startDate, dateRange.endDate],
        queryFn: () => metricsService.getTopQueries(
          selectedSiteId!,
          dateRange.startDate,
          dateRange.endDate,
          10
        ),
        enabled: !!selectedSiteId,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
      },
    ],
  });

  const [timeSeriesQuery, topPagesQuery, topQueriesQuery] = queries;

  const timeSeriesData = timeSeriesQuery.data || [];
  const topPages = topPagesQuery.data || [];
  const topQueries = topQueriesQuery.data || [];
  const loading = queries.some(q => q.isLoading);
  const error = queries.find(q => q.error)?.error as Error | null;

  // Calculate summary metrics with useMemo to avoid unnecessary recalculations
  const summaryMetrics = useMemo(() => {
    if (timeSeriesData.length === 0) {
      return {
        totalClicks: 0,
        totalImpressions: 0,
        avgCtr: 0,
        avgPosition: 0,
        clicksChange: 0,
        impressionsChange: 0,
        ctrChange: 0,
        positionChange: 0,
      };
    }

    const totalClicks = timeSeriesData.reduce((sum, d) => sum + d.clicks, 0);
    const totalImpressions = timeSeriesData.reduce((sum, d) => sum + d.impressions, 0);
    const avgCtr = timeSeriesData.reduce((sum, d) => sum + d.ctr, 0) / timeSeriesData.length;
    const avgPosition = timeSeriesData.reduce((sum, d) => sum + d.position, 0) / timeSeriesData.length;

    // Calculate changes (compare first half vs second half)
    const midpoint = Math.floor(timeSeriesData.length / 2);
    const firstHalf = timeSeriesData.slice(0, midpoint);
    const secondHalf = timeSeriesData.slice(midpoint);

    const firstClicks = firstHalf.reduce((sum, d) => sum + d.clicks, 0);
    const secondClicks = secondHalf.reduce((sum, d) => sum + d.clicks, 0);
    const clicksChange = firstClicks > 0 ? ((secondClicks - firstClicks) / firstClicks) * 100 : 0;

    const firstImpressions = firstHalf.reduce((sum, d) => sum + d.impressions, 0);
    const secondImpressions = secondHalf.reduce((sum, d) => sum + d.impressions, 0);
    const impressionsChange = firstImpressions > 0 ? ((secondImpressions - firstImpressions) / firstImpressions) * 100 : 0;

    const firstCtr = firstHalf.reduce((sum, d) => sum + d.ctr, 0) / firstHalf.length;
    const secondCtr = secondHalf.reduce((sum, d) => sum + d.ctr, 0) / secondHalf.length;
    const ctrChange = firstCtr > 0 ? ((secondCtr - firstCtr) / firstCtr) * 100 : 0;

    const firstPosition = firstHalf.reduce((sum, d) => sum + d.position, 0) / firstHalf.length;
    const secondPosition = secondHalf.reduce((sum, d) => sum + d.position, 0) / secondHalf.length;
    const positionChange = firstPosition > 0 ? ((firstPosition - secondPosition) / firstPosition) * 100 : 0; // Lower is better

    return {
      totalClicks,
      totalImpressions,
      avgCtr,
      avgPosition,
      clicksChange,
      impressionsChange,
      ctrChange,
      positionChange,
    };
  }, [timeSeriesData]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  };

  const handleExportCSV = () => {
    const csvRows = [];
    
    // Header
    csvRows.push(['Date', 'Clicks', 'Impressions', 'CTR (%)', 'Position'].join(','));
    
    // Data rows
    timeSeriesData.forEach(row => {
      csvRows.push([
        row.date,
        row.clicks,
        row.impressions,
        (row.ctr * 100).toFixed(2),
        row.position.toFixed(2)
      ].join(','));
    });
    
    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `metrics-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!selectedSite) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6" color="textSecondary">
          Please select a site from the top menu
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Monitor your site's search performance and experiments
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || 'Failed to load metrics'}
        </Alert>
      )}

      {/* Date Range Picker */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={10}>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateRangeChange}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportCSV}
              disabled={timeSeriesData.length === 0}
              fullWidth
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Clicks"
            value={summaryMetrics.totalClicks.toLocaleString()}
            change={summaryMetrics.clicksChange}
            icon={<Mouse />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Impressions"
            value={summaryMetrics.totalImpressions.toLocaleString()}
            change={summaryMetrics.impressionsChange}
            icon={<Visibility />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Average CTR"
            value={`${(summaryMetrics.avgCtr * 100).toFixed(2)}%`}
            change={summaryMetrics.ctrChange}
            icon={<TrendingUp />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Average Position"
            value={summaryMetrics.avgPosition.toFixed(1)}
            change={summaryMetrics.positionChange}
            icon={<TrendingDown />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : timeSeriesData.length > 0 ? (
        <>
          <MetricsChart data={timeSeriesData} />

          {/* Top Pages and Queries Tables */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Top Pages Table */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Pages by Clicks
                </Typography>
                {topPages.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>URL</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                          <TableCell align="right">Impressions</TableCell>
                          <TableCell align="right">CTR</TableCell>
                          <TableCell align="right">Position</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topPages.map((page, index) => (
                          <TableRow key={index}>
                            <TableCell 
                              sx={{ 
                                maxWidth: 200, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={page.url}
                            >
                              {page.url}
                            </TableCell>
                            <TableCell align="right">{page.clicks.toLocaleString()}</TableCell>
                            <TableCell align="right">{page.impressions.toLocaleString()}</TableCell>
                            <TableCell align="right">{(page.ctr * 100).toFixed(2)}%</TableCell>
                            <TableCell align="right">{page.position.toFixed(1)}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Change History">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => navigate(`/pages/1/changes`)}
                                >
                                  <TimelineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    No page data available
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Top Queries Table */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Queries by Clicks
                </Typography>
                {topQueries.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Query</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                          <TableCell align="right">Impressions</TableCell>
                          <TableCell align="right">CTR</TableCell>
                          <TableCell align="right">Position</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topQueries.map((query, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={query.query}
                            >
                              {query.query}
                            </TableCell>
                            <TableCell align="right">{query.clicks.toLocaleString()}</TableCell>
                            <TableCell align="right">{query.impressions.toLocaleString()}</TableCell>
                            <TableCell align="right">{(query.ctr * 100).toFixed(2)}%</TableCell>
                            <TableCell align="right">{query.position.toFixed(1)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    No query data available
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No data available for the selected period
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Try adjusting the date range or ensure your site is connected to Google Search Console
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;
